import {FloatingFocusManager, FloatingPortal} from '@floating-ui/react';
import i18n from 'i18next';
import {Button, IconButton} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';

import {useRef, useState} from 'react';

import {isNotNil} from 'ramda';

import {suffixTestId, useOnMount, type TestIdProps} from 'shared/web';

import type {useBulkOperationCancellation} from '../../hooks/useBulkOperationCancellation';
import type {useBulkOperationRecovery} from '../../hooks/useBulkOperationRecovery';
import type {BulkOperationResponseBody} from '../../types/api/bulkOperations';
import {type BulkOperationActionDefinitions} from '../../types/BulkOperationActionDefinition';
import type {BulkOperationsServices} from '../../types/BulkOperationsServices';
import {isBulkOperationTerminal} from '../../utils/isBulkOperationTerminal';
import {BulkOperationCard} from './BulkOperationCard';
import {BulkOperationsPanelChip} from './BulkOperationsPanelChip';
import {useBulkOperationsPanelPosition} from './hooks/useBulkOperationsPanelPosition';
import {getBulkOperationPanelSummary} from './utils/getBulkOperationPanelSummary';

const RELATIVE_TIME_REFRESH_MS = 30_000;

type BulkOperationsPanelProps = TestIdProps & {
  operations: BulkOperationResponseBody[];
  history?: {isLoading: boolean; isError: boolean; retry?: () => void};
  actionsDefinition: BulkOperationActionDefinitions;
  services: BulkOperationsServices;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onViewDetail: (bulkOperationId: string) => void;
  recovery: ReturnType<typeof useBulkOperationRecovery>;
  cancellation: ReturnType<typeof useBulkOperationCancellation>;
  onDismiss: (bulkOperationIds: string[]) => void;
};

/** The footer chip plus the anchored panel listing every tracked operation of the grid. */
export function BulkOperationsPanel(props: BulkOperationsPanelProps) {
  const summary = getBulkOperationPanelSummary(props.operations);
  const floating = useBulkOperationsPanelPosition(props);

  return (
    <>
      <BulkOperationsPanelChip
        summary={summary}
        isOpen={props.isOpen}
        buttonProps={{...floating.getReferenceProps(), ref: floating.setReference}}
        data-testid={props['data-testid']}
      />
      <Show when={props.isOpen}>
        <FloatingPortal>
          <FloatingFocusManager context={floating.context} modal={false}>
            <div
              ref={floating.refs.setFloating}
              style={floating.floatingStyles}
              {...floating.getFloatingProps()}
              aria-label={i18n.t('general.actions.bulkActions')}
              className="z-(--z-header-popper) flex w-125 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border! border-solid! border-separator bg-white shadow-xl"
            >
              <BulkOperationsPanelContent {...props} />
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      </Show>
    </>
  );
}

/** Mounted only while the panel is open; ticks so the relative start times stay fresh. */
function BulkOperationsPanelContent(props: BulkOperationsPanelProps) {
  const [, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  useOnMount(
    () => {
      intervalRef.current = setInterval(
        () => setTick((tick) => tick + 1),
        RELATIVE_TIME_REFRESH_MS
      );
    },
    () => {
      clearInterval(intervalRef.current);
    }
  );
  const summary = getBulkOperationPanelSummary(props.operations);
  const terminalIds = props.operations
    .filter((operation) => isBulkOperationTerminal(operation.state))
    .map((operation) => operation.bulkOperationId);
  const summaryParts = [
    summary.runningCount > 0
      ? i18n.t('page.datagrid.bulkOperations.panel.runningCount', {
          count: summary.runningCount,
          defaultValue: '{count} running',
        })
      : null,
    summary.doneCount > 0
      ? i18n.t('page.datagrid.bulkOperations.panel.doneCount', {
          count: summary.doneCount,
          defaultValue: '{count} done',
        })
      : null,
    summary.errorCount > 0
      ? i18n.t('page.datagrid.bulkOperations.panel.errorCount', {
          count: summary.errorCount,
          defaultValue: '{count} with errors',
        })
      : null,
    summary.cancelledCount > 0
      ? i18n.t('page.datagrid.bulkOperations.panel.cancelledCount', {
          count: summary.cancelledCount,
          defaultValue: '{count} cancelled',
        })
      : null,
  ].filter(isNotNil);

  return (
    <div
      className="flex max-h-112 min-h-0 flex-col"
      data-testid={suffixTestId('bulkOperationsPanel', props)}
    >
      <div className="shrink-0 border-b! border-solid! border-separator px-4 py-3">
        <HStack spacing={3} align="center">
          <Box flexGrow={1}>
            <VStack spacing={0}>
              <Text size="base">
                <strong>{i18n.t('general.actions.bulkActions')}</strong>
              </Text>
              <Show
                when={
                  !props.history?.isLoading && !props.history?.isError && summaryParts.length > 0
                }
              >
                <Text size="small" color="secondary">
                  {summaryParts.join(' · ')}
                </Text>
              </Show>
            </VStack>
          </Box>
          <Show when={terminalIds.length > 0}>
            <Button
              title={i18n.t('page.datagrid.bulkOperations.panel.clearFinished', {
                defaultValue: 'Clear finished',
              })}
              variant="link"
              size="small"
              onClick={() => props.onDismiss(terminalIds)}
              data-testid={suffixTestId('clearFinished', props)}
            />
          </Show>
          <IconButton
            icon="navigation/close"
            size="small"
            aria-label={i18n.t('general.actions.close')}
            onClick={props.onClose}
            data-testid={suffixTestId('closePanel', props)}
          />
        </HStack>
      </div>
      <Show when={props.history?.isLoading}>
        <div
          role="status"
          className="px-4 py-3"
          data-testid={suffixTestId('historyLoading', props)}
        >
          <Text size="small">{i18n.t('general.labels.loading')}</Text>
        </div>
      </Show>
      <Show when={props.history?.isError}>
        <div role="alert" className="px-4 py-3" data-testid={suffixTestId('historyError', props)}>
          <Text size="small">{i18n.t('general.labels.somethingWentWrong')}</Text>
          <Show when={props.history?.retry}>
            {(retry) => (
              <Button
                title={i18n.t('general.actions.retry')}
                variant="link"
                size="small"
                onClick={retry}
                data-testid={suffixTestId('retryHistory', props)}
              />
            )}
          </Show>
        </div>
      </Show>
      <div className="min-h-0 overflow-y-auto overscroll-contain">
        {props.operations.map((operation) => (
          <div
            key={operation.bulkOperationId}
            className="border-b! border-solid! border-separator px-4 py-3 last:border-b-0!"
          >
            <BulkOperationCard
              presentation={props.services.presentation}
              operation={operation}
              actionsDefinition={props.actionsDefinition}
              isRerunLoading={props.recovery.pendingIds.has(operation.bulkOperationId)}
              isCancelLoading={props.cancellation.pendingIds.has(operation.bulkOperationId)}
              onViewDetail={() => props.onViewDetail(operation.bulkOperationId)}
              onRerunFailed={() => void props.recovery.rerun(operation)}
              onCancel={() => props.cancellation.cancel(operation.bulkOperationId)}
              onDismiss={() => props.onDismiss([operation.bulkOperationId])}
              data-testid={suffixTestId(`card-${operation.bulkOperationId}`, props)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
